// The Set1 B3/B4 regression class lives in MapObject_ForTerrain.ts: every field the terrain
// pipeline reads from a MapObject must be forwarded explicitly, or it is silently dropped with
// no error.  Adding a field access to the pipeline WITHOUT updating that module raises a
// TypeScript error at the call-site (the return type is the whitelist).
//
// `PackageUpMapObjForTerrain` + the `MapObject_ForTerrain` type are DB-free and live in
// `MapObject_ForTerrain.ts` so the Node test lane can import them without dragging in this
// module's mobx-graphlink / web-vcore browser-runtime tree.  We re-export them here for the
// app's runtime consumers.

import {CreateAccessor} from "mobx-graphlink";
import {ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {ResolveOriginPathForPath, ResolvePathMeshes} from "../../../../../Server/DB/mapObjects/@MapObject/ObjPath/ObjPathResolvers.js";
import {PackageUpMapObjForTerrain} from "./MapObject_ForTerrain.js";
import {PathMeshSet} from "./RasterFeatures_Pure.js";

/** The MobX-cached wrapper around ResolveMapObjForTerrain; in react comps, call this. */
export const ResolveMapObjectsForTerrain = CreateAccessor((
	mapObjects: MapObject[],
	// mobx-accessor caching requires that these be unwrapped
	resolvePathOriginPathIDs: boolean,
	keepPathMeshLocalPoints: boolean,
	calcPathMeshWorldPoints: boolean,
)=>{
	return mapObjects.map(obj=>ResolveMapObjForTerrain(obj, mapObjects, {
		resolvePathOriginPathIDs,
		keepPathMeshLocalPoints,
		calcPathMeshWorldPoints,
	}));
});

export type ResolveMapObjForTerrain_Options = {
}
export function ResolveMapObjForTerrain(
	obj: MapObject,
	mapObjects: MapObject[],
	opts: {
		resolvePathOriginPathIDs: boolean,
		keepPathMeshLocalPoints: boolean,
		calcPathMeshWorldPoints: boolean,
	}
) {
	// todo: find another approach (this one can fail given multiple terrain-defs + it picking the wrong one)
	let worldSeed = 0;
	for (const mo of mapObjects) {
		for (const mod of mo.modifiers ?? []) {
			if (mod.enabled && mod.terrainDefinition) { worldSeed = mod.terrainDefinition.seed; break; }
		}
		if (worldSeed) break;
	}

	const rp = ResolveObjPosInWorld(obj);
	// The object's resolved world rotation (degrees). The renderer wraps a path's visuals in
	// `RotationGroup rotation={-objRot_world_asRadians}` (see MapObjectUI.tsx), so a path's
	// WORLD geometry = worldPos + Rotate(localPoint, -worldRotDeg). The `_meshSet_world` points
	// must apply the SAME rotation or the terrain rasterizer's imprint won't match the rendered
	// shape (an object rotated 45° would still rasterize an axis-aligned square). Jagged edges
	// from the cell grid are expected/acceptable — not applying the rotation at all is not.
	const rp_rotDeg = ResolveObjRotInWorld(obj) ?? 0;
	// Mirrors the renderer: rotation applied counter-clockwise when the angle is NEGATED from
	// the stored clockwise-positive degrees. `-rp_rotDeg` in radians.
	const rp_cos = Math.cos(-rp_rotDeg * (Math.PI / 180));
	const rp_sin = Math.sin(-rp_rotDeg * (Math.PI / 180));

	const resolvedPathOriginPathIDs: Array<string>|n = opts.resolvePathOriginPathIDs ? [] : null;
	const resolvedPathMeshSets: PathMeshSet[]|n = opts.keepPathMeshLocalPoints ? [] : null;
	const resolvedPathMeshSets_world: PathMeshSet[]|n = opts.calcPathMeshWorldPoints ? [] : null;
	// Closed flag per path, collected whenever either mesh set is (the rasterizer reads
	// `_curve_closed_resolved` whenever it reads `_meshSet_world` / `_meshSet`).
	const resolvedPathClosedFlags: Array<boolean>|n = (opts.keepPathMeshLocalPoints || opts.calcPathMeshWorldPoints) ? [] : null;
	// there are still some map-objects in live map that have "paths" missing (despite TS signature)
	for (const path of obj.paths ?? []) {
		if (resolvedPathOriginPathIDs) {
			resolvedPathOriginPathIDs.push(ResolveOriginPathForPath(path, obj)?.path.path_id ?? "");
		}
		// if resolution fails, just resolve it to an empty mesh (we don't want to error on this call-path)
		const pathMeshResolved = ResolvePathMeshes(path, obj);
		const pathMeshSet = pathMeshResolved?.meshes ?? [];
		// The resolved closed flag (the origin path's curve_closed, possibly overridden by
		// inheritance via ResolveInheritedBase) — NOT necessarily this (possibly-inheriting)
		// path's own flag. Collected in parallel so PackageUpMapObjForTerrain emits `_curve_closed_resolved`.
		if (resolvedPathClosedFlags != null) resolvedPathClosedFlags.push(pathMeshResolved?.closed ?? !!path.curve_closed);
		if (resolvedPathMeshSets) {
			resolvedPathMeshSets.push(pathMeshSet.map(mesh=>{
				return mesh.map(pt=>({x: pt[0], y: pt[1]}));
			}));
		}
		if (resolvedPathMeshSets_world) {
			resolvedPathMeshSets_world.push(pathMeshSet.map(mesh=>{
				return mesh.map(pt=>{
					// Apply the object's resolved world rotation, matching the renderer's
					// `RotationGroup` (see MapObjectUI.tsx), then translate to world position.
					const rx = pt[0] * rp_cos - pt[1] * rp_sin;
					const ry = pt[0] * rp_sin + pt[1] * rp_cos;
					return {x: rp.x + rx, y: rp.y + ry};
				});
			}));
		}
	}

	return PackageUpMapObjForTerrain(obj, {
		resolvedWorldPos: rp ? {x: rp.x, y: rp.y} : undefined,
		worldSeed,
	}, {
		resolvedPathOriginPathIDs,
		resolvedPathMeshSets,
		resolvedPathMeshSets_world,
		resolvedPathClosedFlags,
	});
}